import { useContext, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { useShouldEnableMenuEditor } from '@provider-portal/common/experiments/providerOnboarding';
import { useObservableAndState } from '@provider-portal/common/hooks/useObservableAndState';
import { useIsMenuEditorRoute } from '@provider-portal/common/routing/hooks';
import { useCommonAppServices } from '@provider-portal/common/services/ContextProviders';
import { MixpanelEventType } from '@provider-portal/common/services/MixpanelService';
import { usePromotionHistoryCount } from '@provider-portal/common/hooks/usePromotionHistoryCount';

import {
  selectAccountStageOrNewMerchantVenueOnboarding,
  selectIsSelectedProviderNewMerchantVenueInOnboarding,
  selectProviderAction,
  selectSelectedProvider,
} from 'rdx/account';
import { MenuContext } from 'root/navigation/navStructureUtils';

import { useGetModuleInfo, useVenueSelectorConfig } from './hooks';
import { NavBarDrawer } from './NavBarDrawer';
import { NavBarHeader } from './NavBarHeader';
import { NavBarMenuItem } from './NavBarMenu';
import {
  DRAWER_WIDTH,
  flattenAndSortMenuItems,
  pinMenuEditorCategoryAndRemoveSummary,
} from './utils';
import { useIsSelectedProviderInOnboarding } from '@provider-portal/common/hooks/useIsSelectedProviderInOnboarding';
import { LocationAvailabilityStatus } from '@provider-portal/common/api/openapi_gen/deliveryProviderPortal/deliveryProviderPortal/models/LocationAvailabilityStatus';
import { Availability } from '@provider-portal/mp2-common/components/StoreSelector/StoreSelectorStoresList';

export function NavBar() {
  const dispatch = useDispatch();
  const history = useHistory();
  const { mixpanelService, accountService, apiClient } = useCommonAppServices();

  // State
  const [isMobileOpen, setMobileOpen] = useState(false);

  // Context & Selectors
  const menuValue = useContext(MenuContext);
  const accountStage = useSelector(selectAccountStageOrNewMerchantVenueOnboarding);
  const isNewMerchantVenueOnboarding = useSelector(
    selectIsSelectedProviderNewMerchantVenueInOnboarding,
  );
  const selectedProvider = useSelector(selectSelectedProvider);
  const selectedProviderId = useObservableAndState(accountService.getSelectedProviderId$());

  // Route & Feature Flags
  const isMenuEditorRoute = useIsMenuEditorRoute();
  const shouldEnableMenuEditorForSSU = useShouldEnableMenuEditor();
  const shouldEnableMerchantMenuEditor =
    shouldEnableMenuEditorForSSU || isNewMerchantVenueOnboarding;
  const isSelectedProviderInOnboarding = useIsSelectedProviderInOnboarding();

  const historyCount = usePromotionHistoryCount();

  const menuCategoryList = (() => {
    if (!menuValue) {
      throw new Error('NavBar must be rendered inside MenuContext.Provider.');
    }

    let menuCategories = shouldEnableMerchantMenuEditor
      ? pinMenuEditorCategoryAndRemoveSummary(menuValue.menu)
      : menuValue.menu;

    const selfAssignCampaigns = selectedProvider?.permissions?.selfAssignCampaigns;
    const canSeeSponsoredListings = selectedProvider?.permissions?.canSeeSponsoredListings;
    const shouldHidePromotions =
      historyCount !== null &&
      !selfAssignCampaigns &&
      !canSeeSponsoredListings &&
      historyCount === 0;

    if (shouldHidePromotions) {
      const filteredMenu = menuCategories.map((category) => {
        if (category.id === 'marketing') {
          return {
            ...category,
            items: category.items.filter((item) => item.id !== 'promotions'),
          };
        }
        return category;
      });

      menuCategories = filteredMenu.filter((category) => category.items.length > 0);
    }

    return flattenAndSortMenuItems(
      menuCategories,
      accountStage,
      shouldEnableMerchantMenuEditor,
      isMenuEditorRoute,
    );
  })();

  // Event Handlers
  const handleProviderSelect = (storeId: number) => {
    mixpanelService.trackProviderEvent(MixpanelEventType.VENUES_SELECTED, {
      providerId: selectedProviderId,
      selectedVenues: [storeId],
      selectedVenuesCount: 1,
    });
    dispatch(selectProviderAction(storeId));
  };

  const handleAddVenue = () => {
    history.push('/partner-onboarding/new-venue');
  };

  const handleDrawerToggle = () => {
    setMobileOpen((prev) => !prev);
  };

  // Venue selector configuration
  const venueSelector = useVenueSelectorConfig(
    isSelectedProviderInOnboarding,
    handleProviderSelect,
    handleAddVenue,
  );

  // Module info helper
  const getModuleInfo = useGetModuleInfo(menuCategoryList);

  // Menu item click handler with analytics
  const handleMenuItemClick = (menuItem: NavBarMenuItem) => {
    const currentInfo = getModuleInfo(location.pathname);
    const nextInfo = getModuleInfo(menuItem.href);

    if (
      nextInfo.moduleName !== currentInfo.moduleName &&
      location.pathname !== menuItem.href &&
      nextInfo.tabName !== currentInfo.tabName
    ) {
      mixpanelService.trackProviderEvent(MixpanelEventType.SIDE_NAV_PANEL_MODULE_SELECTED, {
        providerId: selectedProviderId,
        moduleName: nextInfo.moduleName,
        currentModuleName: currentInfo.moduleName,
        currentUrl: location.pathname,
        navigatedUrl: menuItem.href,
        isMultiTab: nextInfo.module ? nextInfo.module.tabs.length > 0 : false,
        tabCount: nextInfo.module?.tabs.length ?? 0,
        tabRestorePolicy: 'default',
        tabName: nextInfo.tabName,
        currentTabName: currentInfo.tabName,
        tabRank: nextInfo.tabRank,
        currentTabRank: currentInfo.tabRank,
      });
    }
    handleDrawerToggle();
  };

  const [availabilitiesState, setAvailabilitiesState] = useState({
    availabilities: new Map<number, Availability>(),
    isLoading: true,
  });

  const handleSelectorOpen = () => {
    const currentInfo = getModuleInfo(location.pathname);

    mixpanelService.trackProviderEvent(MixpanelEventType.VENUE_SELECTOR_OPENED, {
      providerId: selectedProviderId,
      venueCount: venueSelector.stores.length,
      selectedVenuesCount: 1,
      selectionMode: 'single',
      currentModuleName: currentInfo.moduleName,
      currentTabName: currentInfo.tabName,
      currentTabRank: currentInfo.tabRank,
    });

    setAvailabilitiesState((currentState) => ({
      availabilities: currentState.availabilities,
      isLoading: false,
    }));

    apiClient.deliveryProviderPortal.getCurrentLocationAvailabilities().then((response) => {
      const data = new Map(
        response.availabilities.map((availability) => [
          availability.provider_id,
          availabilityOf(availability.status),
        ]),
      );
      setAvailabilitiesState({ availabilities: data, isLoading: false });
    });
  };

  const handleSearchPerformed = (
    searchQuery: string,
    resultsCount: number,
    searchCount: number,
  ) => {
    mixpanelService.trackProviderEvent(MixpanelEventType.VENUE_SEARCH_PERFORMED, {
      providerId: selectedProviderId,
      searchQuery: searchQuery,
      queryLength: searchQuery.length,
      matchedVenuesCount: resultsCount,
      searcIteration: searchCount,
    });
  };

  return (
    <>
      <NavBarHeader
        drawerWidth={DRAWER_WIDTH}
        onHandleDrawerToggle={handleDrawerToggle}
        venueSelector={{
          ...venueSelector,
          storeAvailabilities: availabilitiesState.availabilities,
          storeAvailabilitiesLoading: availabilitiesState.isLoading,
        }}
        onSelectorOpen={handleSelectorOpen}
        onSearchPerformed={handleSearchPerformed}
      />
      <nav className="md:w-[211px] md:shrink-0" aria-label="Menu">
        <NavBarDrawer
          onMenuItemClick={handleMenuItemClick}
          isMobileOpen={isMobileOpen}
          onDrawerToggle={handleDrawerToggle}
          drawerWidth={DRAWER_WIDTH}
          menuItems={menuCategoryList}
          venueSelector={{
            ...venueSelector,
            storeAvailabilities: availabilitiesState.availabilities,
            storeAvailabilitiesLoading: availabilitiesState.isLoading,
          }}
          onSelectorOpen={handleSelectorOpen}
          onSearchPerformed={handleSearchPerformed}
        />
      </nav>
    </>
  );
}

function availabilityOf(locationAvailabilityStatus: LocationAvailabilityStatus): Availability {
  switch (locationAvailabilityStatus) {
    case LocationAvailabilityStatus.ONLINE:
      return Availability.ONLINE;
    case LocationAvailabilityStatus.OFFLINE:
      return Availability.OFFLINE;
    case LocationAvailabilityStatus.BUSY:
      return Availability.BUSY;
  }
}
